import { useEffect, useState } from "react";
import { NAV, CONTACT } from "@/constants/testIds";
import { useContact } from "@/context/ContactContext";
import { Menu, X, Apple, Play, Mail } from "lucide-react";

const LINKS = [
  { id: "marketplace", label: "Marketplace", testid: NAV.linkPremium },
  { id: "categories", label: "Categories", testid: NAV.linkCategories },
  { id: "custom", label: "Sell", testid: NAV.linkCustom },
  { id: "shipping", label: "Shipping", testid: NAV.linkShipping },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openContact } = useContact();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-border/60 bg-background/80 backdrop-blur-md"
          : "border-transparent bg-background/40 backdrop-blur-sm"
      }`}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <button
          data-testid={NAV.logo}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5"
          aria-label="Printopia home"
        >
          <img
            src="/logo-mark.jpg"
            alt="Printopia"
            className="h-10 w-10 rounded-lg object-cover ring-1 ring-[rgba(0,229,255,0.35)]"
          />
          <span className="font-display text-lg font-700 font-semibold tracking-tight text-foreground">
            Print<span className="text-neo-cyan">opia</span>
          </span>
        </button>

        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="group relative rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              {l.label}
              <span className="absolute bottom-1 left-3 h-px w-0 bg-[var(--neo-cyan)] transition-[width] duration-300 group-hover:w-[calc(100%-1.5rem)]" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Contact button (top corner) */}
          <button
            data-testid={CONTACT.navButton}
            onClick={openContact}
            aria-label="Contact us"
            className="hidden items-center gap-2 rounded-xl border border-border/70 bg-secondary/50 px-3.5 py-2 text-sm font-medium text-foreground transition-[border-color,color] duration-200 hover:border-[rgba(0,229,255,0.4)] hover:text-foreground sm:inline-flex"
          >
            <Mail className="h-4 w-4 text-neo-cyan" /> Contact
          </button>
          {/* Download app buttons (top-right) */}
          <a
            data-testid={NAV.download}
            href="https://apps.apple.com/us/app/printopia/id6788813110"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
            className="hidden items-center gap-2 rounded-xl border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.14)] px-3.5 py-2 text-sm font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color,transform] duration-200 hover:bg-[rgba(0,229,255,0.22)] active:scale-[0.98] sm:inline-flex"
          >
            <Apple className="h-4 w-4" /> App Store
          </a>
          <button
            data-testid={NAV.downloadPlay}
            onClick={() => go("download")}
            aria-label="Get it on Google Play"
            className="hidden items-center gap-2 rounded-xl border border-border/70 bg-secondary/60 px-3.5 py-2 text-sm font-medium text-foreground transition-[border-color,box-shadow] duration-200 hover:border-[rgba(212,0,255,0.45)] hover:shadow-[var(--glow-magenta)] active:scale-[0.98] lg:inline-flex"
          >
            <Play className="h-4 w-4" /> Google Play
          </button>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border/70 text-foreground md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-border/60 bg-background/95 px-4 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid={CONTACT.navButtonMobile}
              onClick={() => { setOpen(false); openContact(); }}
              className="mt-1 inline-flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground"
            >
              <Mail className="h-4 w-4 text-neo-cyan" /> Contact
            </button>
            <div className="mt-2 flex gap-2">
              <a
                href="https://apps.apple.com/us/app/printopia/id6788813110"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.14)] px-3 py-2 text-sm font-medium text-foreground"
              >
                <Apple className="h-4 w-4" /> App Store
              </a>
              <button
                onClick={() => go("download")}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-border/70 bg-secondary/60 px-3 py-2 text-sm font-medium text-foreground"
              >
                <Play className="h-4 w-4" /> Google Play
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
